/**
 * Sprint Store
 * Manages sprint state using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Sprint, SprintWithStats } from '@/types';

interface SprintState {
  // State
  sprints: Sprint[];
  activeSprint: Sprint | null;
  selectedSprint: Sprint | null;
  sprintStats: SprintWithStats | null;
  currentProjectId: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setSprints: (sprints: Sprint[]) => void;
  setActiveSprint: (sprint: Sprint | null) => void;
  setSelectedSprint: (sprint: Sprint | null) => void;
  setSprintStats: (stats: SprintWithStats | null) => void;
  setCurrentProjectId: (projectId: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  addSprint: (sprint: Sprint) => void;
  updateSprint: (sprint: Sprint) => void;
  removeSprint: (sprintId: string) => void;
  clearSprints: () => void;
}

export const useSprintStore = create<SprintState>()(
  persist(
    (set) => ({
      // Initial state
      sprints: [],
      activeSprint: null,
      selectedSprint: null,
      sprintStats: null,
      currentProjectId: null,
      isLoading: false,
      error: null,

      // Actions
      setSprints: (sprints) => set({ sprints }),

      setActiveSprint: (sprint) => set({ activeSprint: sprint }),

      setSelectedSprint: (sprint) => set({ selectedSprint: sprint }),

      setSprintStats: (stats) => set({ sprintStats: stats }),

      setCurrentProjectId: (projectId) => set({ currentProjectId: projectId }),

      setIsLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      addSprint: (sprint) =>
        set((state) => ({
          sprints: [...state.sprints, sprint],
        })),

      updateSprint: (sprint) =>
        set((state) => ({
          sprints: state.sprints.map((s) =>
            s.sprintId === sprint.sprintId ? sprint : s
          ),
          activeSprint:
            state.activeSprint?.sprintId === sprint.sprintId
              ? sprint
              : state.activeSprint,
          selectedSprint:
            state.selectedSprint?.sprintId === sprint.sprintId
              ? sprint
              : state.selectedSprint,
        })),

      removeSprint: (sprintId) =>
        set((state) => ({
          sprints: state.sprints.filter((s) => s.sprintId !== sprintId),
          activeSprint:
            state.activeSprint?.sprintId === sprintId ? null : state.activeSprint,
          selectedSprint:
            state.selectedSprint?.sprintId === sprintId
              ? null
              : state.selectedSprint,
        })),

      clearSprints: () =>
        set({
          sprints: [],
          activeSprint: null,
          selectedSprint: null,
          sprintStats: null,
        }),
    }),
    {
      name: 'sprint-storage',
      partialize: (state) => ({
        selectedSprint: state.selectedSprint,
      }),
    }
  )
);

// Selectors
export const useSprints = () => useSprintStore((state) => state.sprints);
export const useActiveSprint = () => useSprintStore((state) => state.activeSprint);
export const useSelectedSprint = () => useSprintStore((state) => state.selectedSprint);
export const useSprintStats = () => useSprintStore((state) => state.sprintStats);
export const useSprintLoading = () => useSprintStore((state) => state.isLoading);
export const useSprintError = () => useSprintStore((state) => state.error);

export const useFutureSprints = () =>
  useSprintStore((state) => state.sprints.filter((s) => s.status === 'future'));

export const useClosedSprints = () =>
  useSprintStore((state) => state.sprints.filter((s) => s.status === 'closed'));

export const useSprintById = (sprintId: string | undefined) =>
  useSprintStore((state) =>
    sprintId ? state.sprints.find((s) => s.sprintId === sprintId) : undefined
  );

export default useSprintStore;
